import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { mockBackend } from '../services/mockBackend';
import { User, Lock, Save, Image as ImageIcon, Camera } from 'lucide-react';

const Profile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [photoUrl, setPhotoUrl] = useState(user?.photoUrl || '');
  const [msg, setMsg] = useState<{type: 'success'|'error', text: string}|null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    if (password && password !== confirmPassword) return setMsg({type: 'error', text: 'Senhas não conferem.'});

    try {
      if (password) await mockBackend.changePassword(user!, user!.id, password);
      await mockBackend.updateUser(user!, user!.id, { photoUrl });
      refreshUser({ photoUrl });

      setMsg({type: 'success', text: 'Perfil atualizado.'});
      setPassword(''); setConfirmPassword('');
    } catch (e: any) { setMsg({type: 'error', text: e.message}); }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64 = reader.result as string;
        const uploadResult = await mockBackend.uploadImage(base64, file.name, file.type);
        setPhotoUrl(uploadResult.url);
      } catch (err: any) {
        setMsg({type: 'error', text: "Erro no upload: " + err.message});
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="font-sans animate-in fade-in duration-500">
         <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-sm rounded-lg p-6 flex flex-col md:flex-row gap-8">
            
            <div className="w-full md:w-1/3 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-800 pb-6 md:pb-0 md:pr-8 text-center">
               <div className="w-24 h-24 bg-slate-100 dark:bg-slate-800 rounded-full mx-auto flex items-center justify-center text-slate-400 mb-4 border-2 border-white dark:border-slate-700 shadow-md overflow-hidden relative group">
                  {photoUrl ? (
                      <img src={photoUrl} alt="Perfil" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).src=''; setPhotoUrl(''); }} />
                  ) : (
                      <User size={32} />
                  )}
                  {isUploading && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-xs">...</div>
                  )}
               </div>
               <h2 className="font-bold text-lg text-slate-800 dark:text-slate-200">{user?.name}</h2>
               <p className="text-slate-500 text-sm mb-3">@{user?.username}</p>
               <span className={`px-3 py-0.5 text-xs font-bold uppercase rounded-full ${user?.role === 'admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'}`}>
                  {user?.role === 'admin' ? 'Administrador' : 'Colaborador'}
               </span>
               <div className="mt-6 text-left bg-slate-50 dark:bg-slate-950 p-4 rounded border border-slate-200 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400">
                  <p className="mb-1"><strong>ID:</strong> {user?.id}</p>
                  <p><strong>Acesso:</strong> {new Date().toLocaleDateString()}</p>
               </div>
            </div>

            <div className="flex-1">
               <h3 className="font-bold text-slate-700 dark:text-slate-300 text-sm uppercase border-b border-slate-200 dark:border-slate-800 pb-2 mb-4 flex items-center gap-2">
                  <Lock size={16}/> Dados de Acesso
               </h3>

               {msg && (
                  <div className={`p-3 mb-4 text-xs font-bold rounded border-l-4 ${msg.type === 'success' ? 'bg-green-50 text-green-700 border-green-500 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-50 text-red-700 border-red-500 dark:bg-red-900/30 dark:text-red-400'}`}>
                     {msg.text}
                  </div>
               )}

               <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
                  <div>
                     <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Avatar URL ou Upload</label>
                     <div className="flex items-center border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded overflow-hidden">
                        <div className="pl-3 pr-2 text-slate-400"><ImageIcon size={16}/></div>
                        <input type="text" value={photoUrl} onChange={e => setPhotoUrl(e.target.value)} placeholder="https://..." className="w-full p-2 text-sm bg-transparent text-slate-800 dark:text-slate-200 focus:outline-none" />
                        <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="px-3 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 border-l border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 h-full flex items-center">
                            {isUploading ? '...' : <Camera size={16} />}
                        </button>
                     </div>
                     <input type="file" ref={fileInputRef} onChange={handlePhotoUpload} accept="image/*" className="hidden" />
                  </div>

                  <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                      <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Nova Senha</label>
                      <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••" className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 p-2 rounded text-sm focus:outline-none focus:border-brand-500" />
                  </div>
                  
                  <div>
                      <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Confirmar Senha</label>
                      <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••" className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 p-2 rounded text-sm focus:outline-none focus:border-brand-500" />
                  </div>

                  <div className="pt-4">
                     <button type="submit" disabled={isUploading} className="bg-brand-700 text-white px-6 py-2.5 font-bold uppercase hover:bg-brand-800 flex items-center gap-2 text-xs rounded shadow-sm disabled:opacity-50">
                        <Save size={16} /> Salvar
                     </button>
                  </div>
               </form>
            </div>
         </div>
    </div>
  );
};

export default Profile;